import {
  isVoicePreset,
  MAX_FORMANT,
  MAX_SEMITONES,
  MIN_FORMANT,
  MIN_SEMITONES,
  type VoiceSettings,
} from "@/lib/audio/voices";

export type MicQuality = "speech" | "clear" | "music";

export type MicPreferences = {
  noiseSuppression: boolean;

  echoCancellation: boolean;

  autoGain: boolean;
  quality: MicQuality;

  gain: number;

  clarity: number;

  gate: number;
} & VoiceSettings;

const KEY = "bsmeet.microphone";

export const DEFAULT_MIC_PREFERENCES: MicPreferences = {
  noiseSuppression: true,
  echoCancellation: true,
  autoGain: true,
  quality: "speech",
  gain: 1,
  clarity: 0,
  gate: 0,
  voice: "none",
  voiceSemitones: 0,
  voiceFormant: 1,
};

export const MIN_GAIN = 0.25;
export const MAX_GAIN = 2;

function clamp(value: unknown, low: number, high: number, fallback: number): number {
  if (typeof value !== "number" || Number.isNaN(value)) return fallback;
  return Math.min(high, Math.max(low, value));
}

function asQuality(value: unknown): MicQuality {
  return value === "speech" || value === "clear" || value === "music"
    ? value
    : DEFAULT_MIC_PREFERENCES.quality;
}

function boolean(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function normalise(stored: Partial<MicPreferences>): MicPreferences {
  const fallback = DEFAULT_MIC_PREFERENCES;
  return {
    noiseSuppression: boolean(stored.noiseSuppression, fallback.noiseSuppression),
    echoCancellation: boolean(stored.echoCancellation, fallback.echoCancellation),
    autoGain: boolean(stored.autoGain, fallback.autoGain),
    quality: asQuality(stored.quality),
    gain: clamp(stored.gain, MIN_GAIN, MAX_GAIN, fallback.gain),
    clarity: clamp(stored.clarity, 0, 1, fallback.clarity),
    gate: clamp(stored.gate, 0, 1, fallback.gate),
    voice: isVoicePreset(stored.voice) ? stored.voice : fallback.voice,
    voiceSemitones: clamp(
      stored.voiceSemitones,
      MIN_SEMITONES,
      MAX_SEMITONES,
      fallback.voiceSemitones,
    ),
    voiceFormant: clamp(stored.voiceFormant, MIN_FORMANT, MAX_FORMANT, fallback.voiceFormant),
  };
}

function load(): MicPreferences {
  try {
    const raw = globalThis.localStorage.getItem(KEY);
    if (raw === null) return DEFAULT_MIC_PREFERENCES;
    return normalise(JSON.parse(raw) as Partial<MicPreferences>);
  } catch {
    return DEFAULT_MIC_PREFERENCES;
  }
}

let current: MicPreferences | null = null;
const listeners = new Set<() => void>();

export function getMicPreferences(): MicPreferences {
  current ??= load();
  return current;
}

export function setMicPreference<K extends keyof MicPreferences>(
  key: K,
  value: MicPreferences[K],
): void {
  write({ ...getMicPreferences(), [key]: value });
}

export function resetMicPreferences(): void {
  write(DEFAULT_MIC_PREFERENCES);
}

function write(next: MicPreferences): void {
  current = normalise(next);
  try {
    globalThis.localStorage.setItem(KEY, JSON.stringify(current));
  } catch (error) {
    console.error("microphone preference save failed:", error);
  }
  for (const listener of listeners) listener();
}

export function subscribeMicPreferences(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function micConstraints(preferences: MicPreferences): MediaTrackConstraints {
  return {
    echoCancellation: preferences.echoCancellation,
    noiseSuppression: preferences.noiseSuppression,
    autoGainControl: preferences.autoGain,
    channelCount: preferences.quality === "music" ? 2 : 1,
  };
}

export function micProcessingConstraints(preferences: MicPreferences): MediaTrackConstraints {
  return {
    echoCancellation: preferences.echoCancellation,
    noiseSuppression: preferences.noiseSuppression,
    autoGainControl: preferences.autoGain,
  };
}

export function opusOptions(quality: MicQuality): {
  opusStereo: boolean;
  opusDtx: boolean;
  opusFec: boolean;
  opusMaxAverageBitrate: number;
} {
  switch (quality) {
    case "music":
      return {
        opusStereo: true,
        opusDtx: false,
        opusFec: true,
        opusMaxAverageBitrate: 96_000,
      };
    case "clear":
      return {
        opusStereo: false,
        opusDtx: true,
        opusFec: true,
        opusMaxAverageBitrate: 48_000,
      };
    case "speech":
      return {
        opusStereo: false,
        opusDtx: true,
        opusFec: true,
        opusMaxAverageBitrate: 24_000,
      };
  }
}

export function supportedMicControls(): {
  noiseSuppression: boolean;
  echoCancellation: boolean;
  autoGain: boolean;
} {
  const supported = globalThis.navigator?.mediaDevices?.getSupportedConstraints?.() ?? {};
  return {
    noiseSuppression: supported.noiseSuppression === true,
    echoCancellation: supported.echoCancellation === true,
    autoGain: supported.autoGainControl === true,
  };
}
